/**
 * The anvil in the forge panel (ตีบวก / ตีธาตุ): the weapon laid on it, showing its forging as it
 * looks now (render/forgeFx), and the moment of each strike —
 *
 * - ตีบวก: the hammer comes down three times, sparks flying, then a burst of the tier's light on
 *   success, or a crack and smoke on failure (and a red flare when the weapon slips back a level);
 * - ตีธาตุ: the element's colour swirls in onto the blade and one strike seals it — a flood of
 *   that colour and a ring on success, the swirl scattering into smoke on failure.
 *
 * Realistic pixel art at the rig's double resolution, lit from the upper right. `drawAnvil` is a
 * pure function of time (the review export draws it too); `ForgeAnvil` keeps one canvas that
 * survives the panel being rebuilt, and is ticked from the panel's per-frame refresh.
 */
import { FORGE_ELEMENT_INFO, type ForgeElement, forgeTier, UNFORGED, type WeaponForge } from '@nine/shared';
import { drawForgeFx } from '../render/forgeFx';
import type { BladeLine } from '../render/rig/weapons';

type G = CanvasRenderingContext2D;

/** The scene in art pixels, drawn at two canvas pixels each. */
export const ANVIL_W = 128;
export const ANVIL_H = 46;
const RES = 2;
const P = 0.5;

/** One strike at the anvil. */
export interface AnvilMoment {
  what: 'plus' | 'element';
  ok: boolean;
  /** A failed + that knocked the weapon back a level. */
  slip: boolean;
  el: ForgeElement | null;
  /** The + or element level after the strike. */
  level: number;
  /** When it struck (the clock `drawAnvil` is given). */
  at: number;
  /** The weapon as it was before, shown until the outcome lands. */
  from: WeaponForge;
}

/** How long a strike plays: the hammering, then the outcome. */
const HAMMER = 0.66;
export const MOMENT_SECS = 1.7;

/** The blade on the anvil, for the forging effects. */
const HILT_X = 36;
const BLADE_Y = 25;
const BLADE_LEN = 54;
const BLADE: BladeLine = { hx: HILT_X, hy: BLADE_Y, dx: 1, dy: 0, len: BLADE_LEN, behind: false };
/** Where the hammer meets the blade. */
const HIT = { x: 66, y: BLADE_Y - 1 };

function hash(a: number, b = 0, c = 0): number {
  let h =
    Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x27d4eb2f, 0xc2b2ae35) ^ Math.imul(c, 0x165667b1);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  return (h >>> 0) / 4294967296;
}

function px(g: G, x: number, y: number, col: string, w = P, h = w): void {
  g.fillStyle = col;
  g.fillRect(Math.round(x / P) * P, Math.round(y / P) * P, w, h);
}

/** The smithy behind: dark walls, the forge mouth glowing at the left, coals flickering. */
function backdrop(g: G, t: number): void {
  g.fillStyle = '#151012';
  g.fillRect(0, 0, ANVIL_W, ANVIL_H);
  const flick = 0.85 + 0.15 * Math.sin(t * 7) * Math.sin(t * 3.1);
  // The forge's glow on the wall, in bands.
  for (const [r, a] of [
    [30, 0.06],
    [20, 0.08],
    [11, 0.12],
  ] as const) {
    g.fillStyle = `rgba(230,110,40,${(a * flick).toFixed(3)})`;
    for (let y = ANVIL_H - r; y < ANVIL_H; y += P) {
      const w = Math.sqrt(Math.max(0, r * r - (ANVIL_H - y) ** 2)) * 1.3;
      g.fillRect(0, Math.round(y / P) * P, Math.round(w / P) * P, P);
    }
  }
  // Coals in the corner.
  for (let i = 0; i < 9; i++) {
    const hot = hash(i, Math.floor(t * 6 + i)) < 0.5;
    px(g, 1 + i * 1.5, ANVIL_H - 2 - (i % 3) * P, hot ? '#ffb040' : '#a83a1a', 1, 1);
  }
  // The floor.
  g.fillStyle = '#0e0b0c';
  g.fillRect(0, ANVIL_H - 4, ANVIL_W, 4);
  g.fillStyle = 'rgba(230,120,50,0.08)';
  g.fillRect(0, ANVIL_H - 4, ANVIL_W, P);
}

/** The anvil: forged iron, its face lit from the upper right, the horn to the left. */
function anvil(g: G): void {
  const top = BLADE_Y + 2;
  // Contact shadow on the floor.
  g.fillStyle = 'rgba(0,0,0,0.45)';
  g.fillRect(40, ANVIL_H - 4, 50, 1);
  // Base and waist.
  g.fillStyle = '#2c2a2e';
  g.fillRect(44, ANVIL_H - 8, 42, 4);
  g.fillStyle = '#3c3a40';
  g.fillRect(52, top + 4, 26, ANVIL_H - 8 - top - 4);
  g.fillStyle = '#4c4a52';
  g.fillRect(70, top + 4, 8, ANVIL_H - 8 - top - 4);
  g.fillStyle = '#56545c';
  g.fillRect(84, ANVIL_H - 8, 2, 4);
  // The body under the face, and the horn tapering left.
  g.fillStyle = '#46444c';
  g.fillRect(40, top + 1, 50, 3);
  g.fillStyle = '#2e2c32';
  g.fillRect(40, top + 3.5, 50, P);
  for (let i = 0; i < 12; i++) {
    const h = 3 - i * 0.22;
    g.fillStyle = i < 2 ? '#4a4850' : '#3e3c44';
    g.fillRect(40 - i - 1, top + (3 - h) / 2 + 0.5, 1, Math.max(P, Math.round(h / P) * P));
  }
  // The face: polished steel, brightest on the right.
  g.fillStyle = '#7c7a84';
  g.fillRect(36, top, 54, 1);
  g.fillStyle = '#a8a6b0';
  g.fillRect(66, top, 24, P);
  g.fillStyle = '#d8d6de';
  g.fillRect(84, top, 6, P);
}

/** The weapon itself: grip, guard and a straight blade, lit along its upper edge. `hot` 0 … 1. */
function sword(g: G, hot: number): void {
  const y = BLADE_Y;
  // Grip, wrapped, and the pommel.
  for (let x = HILT_X - 7; x < HILT_X - 1; x += 1) {
    px(g, x, y - P, '#4a2c1c', 1, 1);
    px(g, x + P, y - P, '#2a1810', P, 1);
  }
  px(g, HILT_X - 8.5, y - 1, '#a88848', 1.5, 2);
  px(g, HILT_X - 8.5, y - 1, '#e0c080', P, P);
  // Guard.
  px(g, HILT_X - 1, y - 2.5, '#8a6a34', 1.5, 5);
  px(g, HILT_X - 0.5, y - 2.5, '#d8b46c', P, 5);
  // Blade: lit edge, flat, shadowed edge, tapering to the point.
  for (let x = HILT_X + 0.5; x < HILT_X + BLADE_LEN; x += P) {
    const k = (x - HILT_X) / BLADE_LEN;
    const tip = k > 0.93;
    px(g, x, y - 1, tip && k > 0.97 ? '#c8d0d8' : '#eef2f6');
    if (!(tip && k > 0.97)) px(g, x, y - P, '#a8b2bc');
    if (!tip) px(g, x, y, '#788490');
    if (k < 0.9) px(g, x, y + P, '#4c5560');
  }
  if (hot > 0) {
    // Heated by the hammer: glowing orange from the strike outward.
    g.fillStyle = `rgba(255,${Math.round(150 + hot * 60)},60,${(hot * 0.7).toFixed(2)})`;
    const w = 6 + hot * 18;
    g.fillRect(Math.round((HIT.x - w) / P) * P, y - 1, Math.round((w * 2) / P) * P, 2);
  }
}

/** The hammer: its handle from the smith's grip at the lower right, its head at the end. */
function hammer(g: G, angle: number): void {
  const pivot = { x: 116, y: 40 };
  const reach = 46;
  const head = { x: pivot.x - Math.cos(angle) * reach, y: pivot.y - Math.sin(angle) * reach };
  // Handle: ash wood, lit on its upper side.
  const n = Math.ceil(reach / P);
  for (let i = 0; i <= n - 4; i++) {
    const k = i / n;
    const x = pivot.x + (head.x - pivot.x) * k;
    const y = pivot.y + (head.y - pivot.y) * k;
    px(g, x, y, '#7a5432', P, 1);
    px(g, x, y - P, '#b08a5a');
  }
  // Head: dark iron block, the striking face below, lit at its top right.
  const hx = head.x - 3;
  const hy = head.y - 2.5;
  px(g, hx, hy, '#34323a', 6, 5);
  px(g, hx, hy, '#5c5a64', 6, 1);
  px(g, hx + 4.5, hy, '#9a98a2', 1.5, P);
  px(g, hx, hy + 4.5, '#1c1a20', 6, P);
  px(g, hx + 5.5, hy + 1, '#4a4850', P, 3.5);
}

/** Resting, raised, or striking: the hammer's angle (radians above the line to the blade). */
function hammerAngle(a: number, strikes: readonly number[]): number {
  const down = Math.atan2(40 - HIT.y, 116 - HIT.x);
  for (const s of strikes) {
    const u = (a - s) / 0.22;
    if (u < 0 || u >= 1) continue;
    // Up quickly, then down hard, then a small rebound.
    if (u < 0.5) return down + 0.25 + Math.sin((u / 0.5) * Math.PI * 0.5) * 0.6;
    if (u < 0.65) return down + 0.85 * (1 - (u - 0.5) / 0.15);
    return down + Math.sin(((u - 0.65) / 0.35) * Math.PI) * 0.12;
  }
  return down + 0.55;
}

/** Sparks off the `i`th strike, `age` seconds after it landed. */
function sparks(g: G, i: number, age: number, n: number, tones: readonly string[]): void {
  if (age < 0 || age > 0.55) return;
  for (let k = 0; k < n; k++) {
    const a = -Math.PI * (0.1 + hash(i, k, 1) * 0.8);
    const v = 18 + hash(i, k, 2) * 30;
    const x = HIT.x + Math.cos(a) * v * age;
    const y = HIT.y + Math.sin(a) * v * age + age * age * 60;
    const col = tones[Math.min(tones.length - 1, Math.floor((age / 0.55) * tones.length + hash(i, k, 3)))]!;
    px(g, x, y, col);
    if (age < 0.25) px(g, x - Math.cos(a) * 1, y - Math.sin(a) * 1, col);
  }
  // The flash of the blow.
  if (age < 0.06) {
    px(g, HIT.x - 2, HIT.y - P, '#ffffff', 4, 1);
    px(g, HIT.x - P, HIT.y - 2, '#ffffff', 1, 4);
  }
}

const SPARK_TONES = ['#ffffff', '#fff0b0', '#ffc04a', '#f0701e', '#8a3018'];

/** The colour of a +'s light. */
function tierTone(plus: number): { glow: string; core: string } {
  const tier = forgeTier(plus);
  if (tier === 'black') return { glow: '#8a5ad8', core: '#efe4ff' };
  if (tier === 'red') return { glow: '#ff4a30', core: '#fff0e2' };
  if (tier === 'blue') return { glow: '#50a8ff', core: '#eaf6ff' };
  return plus >= 4 ? { glow: '#8ec4ff', core: '#ffffff' } : { glow: '#e0c27a', core: '#fff6d8' };
}

/** A burst of light from the blade: rays flung out and motes rising. */
function burst(g: G, u: number, glow: string, core: string): void {
  const cx = HILT_X + BLADE_LEN / 2;
  g.save();
  g.globalAlpha = 1 - u;
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2 + 0.1;
    const r0 = 2 + u * 30 * (0.7 + hash(i, 4) * 0.5);
    for (let j = 0; j < 3; j++) {
      const r = r0 - j * 1.5;
      if (r < 1) continue;
      px(g, cx + Math.cos(a) * r * 1.4, BLADE_Y + Math.sin(a) * r * 0.6, j === 0 ? core : glow);
    }
  }
  // The whole blade flaring white, then its colour.
  if (u < 0.3) {
    g.globalAlpha = 1 - u / 0.3;
    g.fillStyle = u < 0.12 ? '#ffffff' : glow;
    g.fillRect(HILT_X, BLADE_Y - 1.5, BLADE_LEN, 3);
  }
  g.globalAlpha = 1 - u * 0.6;
  for (let i = 0; i < 10; i++) {
    const x = HILT_X + 4 + hash(i, 9) * (BLADE_LEN - 4);
    const y = BLADE_Y - 2 - u * (10 + hash(i, 8) * 14);
    px(g, x + Math.sin(u * 8 + i) * 1.2, y, i % 3 ? glow : core);
  }
  g.restore();
}

/** A crack through the blade at the strike, glowing at its edges, and smoke rising off it. */
function crack(g: G, u: number, slip: boolean): void {
  const grow = Math.min(1, u / 0.12);
  // The jagged split, top edge to bottom.
  const pts = [
    [0, -1.5],
    [-1, -0.5],
    [0.5, 0],
    [-0.5, 1],
    [0.5, 1.5],
  ] as const;
  const n = Math.max(1, Math.round(pts.length * grow));
  for (let i = 0; i < n; i++) {
    const [dx, dy] = pts[i]!;
    px(g, HIT.x + dx - P, BLADE_Y + dy, `rgba(255,120,40,${(0.8 * (1 - u)).toFixed(2)})`, 1.5, P);
    px(g, HIT.x + dx, BLADE_Y + dy, '#120c0c');
  }
  // The steel dulled, and on a slip a red flare.
  g.save();
  g.globalAlpha = Math.max(0, 0.45 - u * 0.3);
  g.fillStyle = slip && u < 0.4 ? '#c0281c' : '#2a2626';
  g.fillRect(HILT_X, BLADE_Y - 1, BLADE_LEN, 2);
  g.restore();
  // Smoke: grey puffs swelling as they rise and thin.
  for (let i = 0; i < 7; i++) {
    const life = Math.min(1, Math.max(0, u * 1.3 - hash(i, 2) * 0.3));
    if (life <= 0 || life >= 1) continue;
    const x = HIT.x + (hash(i, 3) - 0.5) * 10 + Math.sin(life * 5 + i) * 2;
    const y = BLADE_Y - 2 - life * 16;
    const w = 1.5 + life * 3;
    const a = (Math.sin(life * Math.PI) * 0.55).toFixed(2);
    px(g, x - w / 2, y - w / 2, `rgba(96,90,88,${a})`, w, w);
    px(g, x - w / 2 + P, y - w / 2, `rgba(150,144,140,${a})`, w - 1, P);
  }
}

/** The element's colour spiralling in onto the blade (`u` 0 … 1), or scattering off it (`out`). */
function swirl(g: G, el: ForgeElement, u: number, out: boolean): void {
  const col = FORGE_ELEMENT_INFO[el].color;
  const cx = HIT.x;
  for (let k = 0; k < 10; k++) {
    for (let j = 0; j < 4; j++) {
      const v = Math.max(0, u - j * 0.03);
      const r = out ? 4 + v * 34 : 30 * (1 - v) + 2;
      const a = (k / 10) * Math.PI * 2 + (out ? 0.5 + v * 1.5 : v * 7);
      const x = cx + Math.cos(a) * r * 1.5;
      const y = BLADE_Y + Math.sin(a) * r * 0.55;
      g.globalAlpha = (out ? 1 - v : Math.min(1, v * 3)) * (1 - j * 0.22);
      if (j === 0) {
        // A mote of the element: its colour, lit at the upper right.
        px(g, x - P, y - P, col, 1.5, 1.5);
        px(g, x, y - P, '#ffffff');
      } else px(g, x, y, col, j === 1 ? 1 : P, j === 1 ? 1 : P);
    }
  }
  g.globalAlpha = 1;
}

/** An element sealed into the blade: its colour flooding along the steel, a ring opening out. */
function seal(g: G, el: ForgeElement, u: number): void {
  const col = FORGE_ELEMENT_INFO[el].color;
  g.save();
  g.globalAlpha = (1 - u) * 0.7;
  g.fillStyle = col;
  const w = Math.min(1, u * 4) * BLADE_LEN;
  g.fillRect(HIT.x - w / 2, BLADE_Y - 1, w, 2);
  g.globalAlpha = 1 - u;
  const r = 3 + u * 26;
  for (let i = 0; i < 40; i++) {
    const a = (i / 40) * Math.PI * 2;
    px(g, HIT.x + Math.cos(a) * r * 1.5, BLADE_Y + Math.sin(a) * r * 0.5, i % 4 ? col : '#ffffff');
  }
  g.restore();
}

/**
 * Draws the anvil scene at time `t` (seconds, any clock): the weapon forged as `forge` with its
 * effects, and the strike `moment` if it is still playing. `g` is a canvas `ANVIL_W`×`ANVIL_H` art
 * pixels at two canvas pixels each.
 */
export function drawAnvil(g: G, forge: WeaponForge, moment: AnvilMoment | null, t: number): void {
  g.save();
  g.setTransform(RES, 0, 0, RES, 0, 0);
  g.imageSmoothingEnabled = false;
  backdrop(g, t);
  anvil(g);
  const a = moment ? t - moment.at : Infinity;
  const playing = moment && a >= 0 && a < MOMENT_SECS ? moment : null;
  const strikes = playing?.what === 'element' ? [HAMMER - 0.22] : [0, 0.22, 0.44];
  const angle = hammerAngle(playing ? a : -1, playing ? strikes : []);
  // The blade glows hot under the hammer, cooling after.
  const hot = playing
    ? Math.max(0, ...strikes.map((s) => (a >= s + 0.143 ? Math.max(0, 1 - (a - s - 0.143) / 0.5) : 0)))
    : 0;
  // While the hammer falls the weapon shows what it was; the outcome changes it.
  const shown = playing && a < HAMMER ? playing.from : forge;
  drawForgeFx(g, [BLADE], shown, t, 'back', 3);
  sword(g, hot);
  drawForgeFx(g, [BLADE], shown, t, 'front', 3);
  if (playing) {
    if (playing.what === 'element' && playing.el && a < HAMMER) swirl(g, playing.el, a / HAMMER, false);
    for (const [i, s] of strikes.entries())
      sparks(g, i + Math.floor(playing.at * 10), a - s - 0.143, 12, SPARK_TONES);
    const u = (a - HAMMER) / (MOMENT_SECS - HAMMER);
    if (u >= 0) {
      if (playing.what === 'plus') {
        if (playing.ok) {
          const tone = tierTone(playing.level);
          burst(g, u, tone.glow, tone.core);
        } else crack(g, u, playing.slip);
      } else if (playing.el) {
        if (playing.ok) seal(g, playing.el, u);
        else {
          swirl(g, playing.el, u, true);
          crack(g, Math.min(1, u * 1.4), false);
        }
      }
    }
  }
  hammer(g, angle);
  g.restore();
}

/** What an element at level `lv` shows on the weapon (render/forgeFx `elStrength`). */
export function elementShows(lv: number): string {
  if (lv >= 8) return 'ธาตุเต็มกำลัง วนรอบอาวุธ ทิ้งรอย และเรืองถึงพื้น';
  if (lv >= 7) return 'ธาตุวนรอบอาวุธ ทิ้งรอย เรืองถึงพื้น · ขั้น 8 เต็มกำลัง';
  if (lv >= 5) return 'ธาตุวนรอบอาวุธ · ขั้น 7 ทิ้งรอยและเรืองถึงพื้น';
  if (lv >= 3) return 'ธาตุเรืองบนคมอาวุธ · ขั้น 5 จะวนรอบอาวุธ';
  return 'ประกายธาตุจาง ๆ · ขั้น 3 เรืองบนคม ขั้น 5 วนรอบอาวุธ';
}

/** The words over the anvil once the outcome lands. */
function verdict(m: AnvilMoment): string {
  if (m.what === 'element') {
    const name = m.el ? FORGE_ELEMENT_INFO[m.el].name : '';
    return m.ok ? `ธาตุ${name} ขั้น ${m.level}` : `ธาตุ${name}ไม่ติด`;
  }
  return m.ok ? `สำเร็จ +${m.level}` : m.slip ? `ล้มเหลว · ลดเหลือ +${m.level}` : 'ล้มเหลว';
}

/** The anvil's canvas in the panel, kept across rebuilds, and the strike playing on it. */
export class ForgeAnvil {
  readonly root: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly flash: HTMLElement;
  private readonly g: G;
  private item = '';
  private forge: WeaponForge = UNFORGED;
  private moment: AnvilMoment | null = null;
  private shown: AnvilMoment | null = null;

  constructor() {
    this.root = document.createElement('div');
    this.root.className = 'forge-anvil';
    this.canvas = document.createElement('canvas');
    this.canvas.width = ANVIL_W * RES;
    this.canvas.height = ANVIL_H * RES;
    this.canvas.setAttribute('aria-hidden', 'true');
    this.flash = document.createElement('b');
    this.flash.className = 'forge-flash';
    this.flash.setAttribute('role', 'status');
    this.root.append(this.canvas, this.flash);
    this.g = this.canvas.getContext('2d')!;
  }

  /** Shows the weapon `id` as forged now (a strike playing on another weapon stops). */
  show(id: string, forge: WeaponForge): void {
    if (id !== this.item) this.moment = null;
    this.item = id;
    this.forge = forge;
  }

  /** A strike just landed on weapon `id` (its id after the strike). */
  strike(id: string, m: AnvilMoment): void {
    this.item = id;
    this.moment = m;
  }

  /** Draws the current frame; cheap, and nothing at all while the anvil is off screen. */
  tick(t: number): void {
    if (!this.root.isConnected) return;
    const m = this.moment;
    drawAnvil(this.g, this.forge, m, t);
    // The verdict appears as the hammering ends.
    const due = m && t - m.at >= HAMMER && t - m.at < MOMENT_SECS ? m : null;
    if (due !== this.shown) {
      this.shown = due;
      if (due) {
        this.flash.textContent = verdict(due);
        this.flash.className = `forge-flash ${due.ok ? 'ok' : 'fail'}`;
        if (due.ok && due.what === 'plus') this.flash.style.setProperty('--tone', tierTone(due.level).glow);
        else if (due.el) this.flash.style.setProperty('--tone', FORGE_ELEMENT_INFO[due.el].color);
        else this.flash.style.removeProperty('--tone');
      } else this.flash.className = 'forge-flash';
    }
  }
}
